/**
 * Deliverables modal — brand-internal assembly, client feedback, and ?adId
 * deep-link support.
 *
 * Gated by betaFlags.weekly_deliverables AND (isMaster || user visibility).
 *
 * Aligned with the canonical server surface in server/deliverables-routes.ts
 * (namespace: /api/deliverables/*). All payload parsing is defensive: the
 * server may return either a bare delivery object or a { delivery } wrapper,
 * and DTO field names vary (projectNameSnapshot vs projectName, itemAdId vs
 * id), so we normalize both here.
 *
 * Tabs:
 *   1. Deliveries   — draft assembly: add/remove PROJECTS (project-level
 *                     curation — never individual ads), team note, due date,
 *                     eligible ad counts/thumbnails, publish summary + confirm.
 *   2. Client feedback — per-project approved/rejected/unanswered, ranked reject
 *                        reasons, future-feedback/drop queues, reviewer comments/pins,
 *                        close action when all items are done.
 */
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2, PackageOpen, CheckCircle2, XCircle,
  Plus, Minus, Calendar, Send, BarChart3, ChevronDown,
  ChevronUp, MapPin, ThumbsUp,
  Lock, Inbox, ImageOff, History, ExternalLink, Trash2, Ban, Link2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { invalidateApprovalStates } from "@/lib/approval-states";
export { useApprovalStates } from "@/hooks/use-approval-states";
import {
  DELIVERABLE_REJECT_REASONS,
  DELIVERABLE_POSITIVE_TAGS,
  summarizeDeliverable,
  resolvedDecisionsByAd,
  type AdDecisionValue,
} from "@shared/deliverables";
import type { Project } from "@shared/schema";

// ─── API types (normalized client shapes) ──────────────────────────────────

export type AdApprovalStatus =
  | "current"
  | "earlier"
  | "rejected-current"
  | "rejected-earlier";

type DeliveryAdRow = {
  itemAdId: string;   // delivery_item_ad.id
  adId: string | null;
  displayName: string;
  imageUrl: string | null;
  aspectRatio: string | null;
  sortOrder: number;
  decisions: DecisionRow[];
};

type DeliveryItemRow = {
  id: string;
  projectId: string | null;
  projectName: string;
  note: string | null;
  sortOrder: number;
  submittedAt: string | null;
  ads: DeliveryAdRow[];
  projectRejectedAt: string | null;
  projectRejectedBy: string | null;
  projectRejectedByLabel: string | null;
};

type DecisionRow = {
  id: string;
  deliveryItemAdId: string;
  authorId: string | null;
  authorLabel: string | null;
  decision: AdDecisionValue;
  reasons: string[];
  comment: string | null;
  disposition: "rework" | "drop" | null;
  positiveTags: string[];
  pinX: number | null;
  pinY: number | null;
  imageUrlSnapshot: string | null;
  createdAt: string | null;
};

type WeeklyDelivery = {
  id: string;
  brandId: string;
  title: string;
  note: string | null;
  status: "draft" | "delivered" | "closed" | "revoked";
  dueAt: string | null;
  publishedAt: string | null;
  closedAt: string | null;
  createdAt: string | null;
  items: DeliveryItemRow[];
};

// Eligible project: projects with saved ads that aren't already in the draft.
type EligibleProject = {
  projectId: string;
  projectName: string;
  adCount: number;
  adIds: string[];
  thumbnails: Array<{ adId: string; imageUrl: string | null }>;
};

// Raw server DTO shapes (loosely typed — we normalize defensively).
type RawAny = Record<string, any>;

// ─── Defensive normalizers ─────────────────────────────────────────────────

/** Some endpoints wrap the payload in { delivery }, others return it bare. */
function unwrapDelivery(raw: RawAny | null | undefined): RawAny | null {
  if (!raw) return null;
  if (raw.delivery !== undefined) return raw.delivery ?? null;
  return raw;
}

function normalizeDecision(raw: RawAny): DecisionRow {
  return {
    id: String(raw.id ?? ""),
    deliveryItemAdId: String(raw.deliveryItemAdId ?? raw.itemAdId ?? ""),
    authorId: raw.authorId ?? null,
    authorLabel: raw.authorLabel ?? null,
    decision: (raw.decision === "rejected" ? "rejected" : "approved") as AdDecisionValue,
    reasons: Array.isArray(raw.reasons) ? raw.reasons.map(String) : [],
    comment: raw.comment ?? null,
    disposition:
      raw.disposition === "drop" ? "drop" : raw.disposition === "rework" ? "rework" : null,
    positiveTags: Array.isArray(raw.positiveTags) ? raw.positiveTags.map(String) : [],
    pinX: typeof raw.pinX === "number" ? raw.pinX : null,
    pinY: typeof raw.pinY === "number" ? raw.pinY : null,
    imageUrlSnapshot: raw.imageUrlSnapshot ?? null,
    createdAt: raw.createdAt ?? null,
  };
}

function normalizeAd(raw: RawAny): DeliveryAdRow {
  // Server may send itemAdId (serializeDeliverableReviewAd) or id.
  const itemAdId = String(raw.itemAdId ?? raw.id ?? "");
  return {
    itemAdId,
    adId: raw.adId ?? null,
    displayName:
      (raw.displayName ?? raw.displayNameSnapshot ?? "").toString().trim() || "Untitled ad",
    imageUrl: raw.imageUrl ?? raw.imageUrlSnapshot ?? null,
    aspectRatio: raw.aspectRatio ?? raw.aspectRatioSnapshot ?? null,
    sortOrder: typeof raw.sortOrder === "number" ? raw.sortOrder : 0,
    decisions: Array.isArray(raw.decisions)
      ? raw.decisions.map((d: RawAny) => {
          const dec = normalizeDecision(d);
          // Ensure the decision points at this itemAdId so resolvedDecisionsByAd
          // can group by ad even when the server omits the back-reference.
          if (!dec.deliveryItemAdId) dec.deliveryItemAdId = itemAdId;
          return dec;
        })
      : [],
  };
}

function normalizeItem(raw: RawAny): DeliveryItemRow {
  const flatDecisions = Array.isArray(raw.decisions)
    ? raw.decisions.map(normalizeDecision)
    : [];
  const ads = Array.isArray(raw.ads)
    ? raw.ads.map((rawAd: RawAny) => {
        const ad = normalizeAd(rawAd);
        if (ad.decisions.length === 0) {
          ad.decisions = flatDecisions.filter(
            (decision) => decision.deliveryItemAdId === ad.itemAdId,
          );
        }
        return ad;
      })
    : [];
  return {
    id: String(raw.id ?? ""),
    projectId: raw.projectId ?? null,
    projectName:
      (raw.projectNameSnapshot ?? raw.projectName ?? "").toString().trim() || "Untitled project",
    note: raw.note ?? null,
    sortOrder: typeof raw.sortOrder === "number" ? raw.sortOrder : 0,
    submittedAt: raw.submittedAt ?? null,
    ads,
    projectRejectedAt: raw.projectRejectedAt ?? null,
    projectRejectedBy: raw.projectRejectedBy ?? null,
    projectRejectedByLabel: raw.projectRejectedByLabel ?? null,
  };
}

function normalizeDelivery(raw: RawAny | null | undefined): WeeklyDelivery | null {
  const d = unwrapDelivery(raw);
  if (!d || !d.id) return null;
  const status: WeeklyDelivery["status"] =
    d.status === "draft" || d.status === "delivered" || d.status === "closed" || d.status === "revoked"
      ? d.status
      : "draft";
  return {
    id: String(d.id),
    brandId: String(d.brandId ?? ""),
    title: (d.title ?? "").toString() || "Untitled delivery",
    note: d.note ?? null,
    status,
    dueAt: d.dueAt ?? null,
    publishedAt: d.publishedAt ?? null,
    closedAt: d.closedAt ?? null,
    createdAt: d.createdAt ?? null,
    items: Array.isArray(d.items) ? d.items.map(normalizeItem) : [],
  };
}

/** Flatten all per-ad decisions on an item into a single decisions array
 *  keyed by deliveryItemAdId, for summarize/resolve helpers. */
function itemDecisions(item: DeliveryItemRow): DecisionRow[] {
  return item.ads.flatMap((a) =>
    a.decisions.map((d) => ({ ...d, deliveryItemAdId: d.deliveryItemAdId || a.itemAdId })),
  );
}
// ─── Helpers ───────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return "—";
  }
}

function fmtDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
}

function fmtSelectorDate(delivery: WeeklyDelivery): string {
  if (delivery.dueAt) return `Due ${fmtDate(delivery.dueAt)}`;
  if (!delivery.createdAt) return "Date not set";
  try {
    return `Created ${new Date(delivery.createdAt).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })}`;
  } catch {
    return "Date not set";
  }
}

/** Format today's date as "Delivery — Jul 28, 2025" */
function defaultDeliveryTitle(): string {
  const now = new Date();
  return `Delivery — ${now.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
}

const REASON_LABELS: Record<string, string> = Object.fromEntries(
  DELIVERABLE_REJECT_REASONS.map((r) => [r.key, r.label]),
);
const POSITIVE_LABELS: Record<string, string> = Object.fromEntries(
  DELIVERABLE_POSITIVE_TAGS.map((t) => [t.key, t.label]),
);

/** Retain delivery refreshes, but approval badges only refresh for this brand. */
function invalidateDeliveries(brandId: string) {
  invalidateApprovalStates(queryClient, brandId);
  queryClient.invalidateQueries({
    predicate: (q) =>
      Array.isArray(q.queryKey) &&
      typeof q.queryKey[0] === "string" &&
      !q.queryKey[0].endsWith("/weekly-deliveries/approved-ads") &&
      (
        (q.queryKey[0] as string).startsWith(`/api/deliverables`) ||
        (q.queryKey[0] as string).startsWith(`/api/brands`)
      ),
  });
}

/** Status badge label/class for a delivery. */
function deliveryStatusLabel(status: WeeklyDelivery["status"]): string {
  switch (status) {
    case "draft": return "Draft";
    case "delivered": return "In Review";
    case "closed": return "Closed";
    case "revoked": return "Revoked";
  }
}

function deliveryStatusVariant(status: WeeklyDelivery["status"]): "outline" | "default" | "secondary" | "destructive" {
  switch (status) {
    case "draft": return "outline";
    case "delivered": return "default";
    case "closed": return "secondary";
    case "revoked": return "destructive";
  }
}

// ─── Small sub-components ──────────────────────────────────────────────────

const AdThumbnail = memo(function AdThumbnail({
  src, alt, size = 48,
}: { src: string | null; alt: string; size?: number }) {
  if (!src) {
    return (
      <div
        className="shrink-0 bg-muted/50 rounded flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <ImageOff className="h-4 w-4 text-muted-foreground" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      className="shrink-0 rounded object-cover bg-muted/50"
      style={{ width: size, height: size }}
      loading="lazy"
    />
  );
});

function DecisionBadge({ decision }: { decision: AdDecisionValue | null }) {
  if (!decision) return null;
  if (decision === "approved") {
    return (
      <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white border-transparent gap-1 text-[10px]">
        <CheckCircle2 className="h-3 w-3" /> Approved
      </Badge>
    );
  }
  return (
    <Badge className="bg-destructive hover:bg-destructive text-destructive-foreground border-transparent gap-1 text-[10px]">
      <XCircle className="h-3 w-3" /> Rejected
    </Badge>
  );
}

/** Canonical client-feedback badges shared by the ad library and results view. */
export function ApprovalBadge({
  status, adId,
}: { status: AdApprovalStatus | null | undefined; adId?: string }) {
  if (status === "current") {
    return (
      <Badge
        className="bg-emerald-600 hover:bg-emerald-600 text-white border-transparent gap-1 text-[10px]"
        data-testid={adId ? `badge-client-approved-${adId}` : undefined}
      >
        <CheckCircle2 className="h-3 w-3" /> Approved
      </Badge>
    );
  }
  if (status === "earlier") {
    return (
      <Badge
        variant="outline"
        className="text-[10px] gap-1 text-muted-foreground border-muted-foreground/30"
        data-testid={adId ? `badge-approved-earlier-${adId}` : undefined}
      >
        <History className="h-3 w-3" /> Approved (earlier version)
      </Badge>
    );
  }
  if (status === "rejected-current") {
    return (
      <Badge
        className="bg-destructive hover:bg-destructive text-destructive-foreground border-transparent gap-1 text-[10px]"
        data-testid={adId ? `badge-client-rejected-${adId}` : undefined}
      >
        <XCircle className="h-3 w-3" /> Rejected
      </Badge>
    );
  }
  if (status === "rejected-earlier") {
    return (
      <Badge
        variant="outline"
        className="text-[10px] gap-1 text-destructive border-destructive/40"
        data-testid={adId ? `badge-rejected-earlier-${adId}` : undefined}
      >
        <History className="h-3 w-3" /> Rejected (earlier version)
      </Badge>
    );
  }
  return null;
}

// ─── Scoreboard per-project card ──────────────────────────────────────────

function ProjectScoreCard({
  item,
  approvalStates,
  deepLinkAdId,
  onOpenAd,
}: {
  item: DeliveryItemRow;
  approvalStates: Map<string, AdApprovalStatus>;
  deepLinkAdId: string | null;
  onOpenAd: (adId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const decisions = useMemo(() => itemDecisions(item), [item]);

  const resolvedMap = useMemo(
    () => resolvedDecisionsByAd(decisions),
    [decisions],
  );

  const summary = useMemo(
    () => summarizeDeliverable({ adCount: item.ads.length, decisions }),
    [item.ads.length, decisions],
  );

  const reviewerNames = useMemo(() => {
    const approved = new Set<string>();
    const rejected = new Set<string>();
    for (const decision of decisions) {
      const name = decision.authorLabel?.trim() || "Reviewer";
      if (decision.decision === "approved") approved.add(name);
      else rejected.add(name);
    }
    return {
      approved: [...approved],
      rejected: [...rejected],
    };
  }, [decisions]);

  // Ranked reject reasons across all rejected decisions for this project.
  const rankedReasons = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const dec of decisions) {
      if (dec.decision !== "rejected") continue;
      for (const r of dec.reasons ?? []) {
        counts[r] = (counts[r] ?? 0) + 1;
      }
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([key, count]) => ({ key, label: REASON_LABELS[key] ?? key, count }));
  }, [decisions]);

  // Apply-to-future-work vs Drop queue. The persisted "rework" key is stable;
  // only its user-facing wording reflects that no current creative edit happens here.
  const reworkAds = useMemo(
    () =>
      item.ads.filter((a) => {
        const r = resolvedMap.get(a.itemAdId);
        return r?.decision === "rejected" && r.winningDecision?.disposition !== "drop";
      }),
    [item.ads, resolvedMap],
  );
  const dropAds = useMemo(
    () =>
      item.ads.filter((a) => {
        const r = resolvedMap.get(a.itemAdId);
        return r?.decision === "rejected" && r.winningDecision?.disposition === "drop";
      }),
    [item.ads, resolvedMap],
  );

  return (
    <Card className="p-3 space-y-3" data-testid={`scoreboard-item-${item.id}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{item.projectName}</p>
          {item.submittedAt && (
            <p className="text-[11px] text-muted-foreground">
              Submitted {fmtDate(item.submittedAt)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
          {summary.approved > 0 && (
            <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white border-transparent gap-1 text-[10px]">
              <CheckCircle2 className="h-3 w-3" />
              {summary.approved} approved
            </Badge>
          )}
          {summary.rejected > 0 && (
            <Badge className="bg-destructive hover:bg-destructive text-destructive-foreground gap-1 text-[10px]">
              <XCircle className="h-3 w-3" />
              {summary.rejected} rejected
            </Badge>
          )}
          {summary.unanswered > 0 && (
            <Badge variant="secondary" className="gap-1 text-[10px]">
              <Inbox className="h-3 w-3" />
              {summary.unanswered} unanswered
            </Badge>
          )}
        </div>
      </div>

      {(reviewerNames.approved.length > 0 || reviewerNames.rejected.length > 0) && (
        <div
          className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground"
          data-testid={`reviewer-summary-${item.id}`}
        >
          {reviewerNames.approved.length > 0 && (
            <p>
              <span className="font-medium text-emerald-700 dark:text-emerald-400">Approved by:</span>{" "}
              {reviewerNames.approved.join(", ")}
            </p>
          )}
          {reviewerNames.rejected.length > 0 && (
            <p>
              <span className="font-medium text-destructive">Rejected by:</span>{" "}
              {reviewerNames.rejected.join(", ")}
            </p>
          )}
        </div>
      )}

      {item.projectRejectedAt && (
        <div
          className="rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-2 text-[11px] text-destructive"
          data-testid={`project-rejected-by-${item.id}`}
        >
          <span className="font-medium">Entire project rejected by:</span>{" "}
          {item.projectRejectedByLabel || "Reviewer"}
        </div>
      )}

      {/* Ranked rejection reasons */}
      {rankedReasons.length > 0 && (
        <div className="space-y-1">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
            Top rejection reasons
          </p>
          <div className="flex flex-wrap gap-1">
            {rankedReasons.map(({ key, label, count }) => (
              <Badge
                key={key}
                variant="outline"
                className="text-[10px] gap-1 text-destructive border-destructive/30"
                data-testid={`reason-${item.id}-${key}`}
              >
                {label} × {count}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Per-ad row list */}
      <button
        type="button"
        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`button-expand-${item.id}`}
      >
        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {expanded ? "Hide ads" : `Show ${item.ads.length} ad${item.ads.length === 1 ? "" : "s"}`}
      </button>

      {expanded && (
        <div className="space-y-2">
          {item.ads.map((a) => {
            const resolved = resolvedMap.get(a.itemAdId);
            const dec = resolved?.winningDecision as DecisionRow | undefined;
            const attributedDecisions = decisions.filter(
              (decision) => decision.deliveryItemAdId === a.itemAdId,
            );
            const isDeepLink = deepLinkAdId === a.adId;
            // Cross-delivery approval state (current vs earlier version).
            const approvalStatus = a.adId ? approvalStates.get(a.adId) ?? null : null;

            return (
              <div
                key={a.itemAdId}
                className={[
                  "flex items-start gap-2 rounded-md border p-2",
                  isDeepLink ? "ring-2 ring-primary" : "",
                ].join(" ")}
                data-testid={`scoreboard-ad-${a.itemAdId}`}
                id={isDeepLink ? `scoreboard-ad-deeplink` : undefined}
              >
                <AdThumbnail src={a.imageUrl} alt={a.displayName} size={40} />
                <div className="flex-1 min-w-0 space-y-0.5">
                  <p className="text-xs font-medium truncate">{a.displayName}</p>
                  <div className="flex flex-wrap items-center gap-1">
                    <DecisionBadge decision={resolved?.decision ?? null} />
                    {/* Surface cross-delivery feedback only when it adds a
                        different signal. The row's own rejection already has
                        a DecisionBadge, so don't duplicate it with the global
                        Rejected badge. */}
                    {(
                      !resolved?.decision ||
                      (
                        resolved.decision === "rejected" &&
                        (approvalStatus === "current" || approvalStatus === "earlier")
                      )
                    ) && (
                      <ApprovalBadge status={approvalStatus} adId={a.adId ?? undefined} />
                    )}
                    {dec?.disposition === "drop" && (
                      <Badge
                        variant="outline"
                        className="text-[10px] text-muted-foreground"
                        data-testid={`badge-disposition-${a.itemAdId}`}
                      >
                        Drop
                      </Badge>
                    )}
                    {dec?.disposition === "rework" && (
                      <Badge
                        variant="outline"
                        className="text-[10px] text-amber-600 border-amber-500/30"
                        data-testid={`badge-rework-${a.itemAdId}`}
                      >
                        Use feedback going forward
                      </Badge>
                    )}
                  </div>
                  {dec?.reasons && dec.reasons.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      {dec.reasons.map((r) => REASON_LABELS[r] ?? r).join(", ")}
                    </p>
                  )}
                  {dec?.comment && (
                    <p className="text-[11px] text-muted-foreground italic truncate">
                      "{dec.comment}"
                    </p>
                  )}
                  {dec?.positiveTags && dec.positiveTags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {dec.positiveTags.map((t) => (
                        <Badge
                          key={t}
                          variant="outline"
                          className="text-[10px] text-emerald-600 border-emerald-500/30 gap-1"
                        >
                          <ThumbsUp className="h-3 w-3" />
                          {POSITIVE_LABELS[t] ?? t}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {dec?.pinX != null && dec.pinY != null && (
                    <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
                      <MapPin className="h-3 w-3" />
                      Pinned comment
                    </span>
                  )}
                  {attributedDecisions.length > 0 && (
                    <div className="space-y-1 pt-1" data-testid={`reviewers-${a.itemAdId}`}>
                      {attributedDecisions.map((decision) => (
                        <div
                          key={decision.id}
                          className="rounded border border-border/60 bg-muted/30 px-2 py-1"
                        >
                          <p className="text-[10px] font-medium">
                            {decision.decision === "approved" ? "Approved by" : "Rejected by"}{" "}
                            {decision.authorLabel || "Reviewer"}
                          </p>
                          {decision.reasons.length > 0 && (
                            <p className="text-[10px] text-muted-foreground">
                              {decision.reasons.map((reason) => REASON_LABELS[reason] ?? reason).join(", ")}
                            </p>
                          )}
                          {decision.positiveTags.length > 0 && (
                            <p className="text-[10px] text-emerald-700 dark:text-emerald-400">
                              {decision.positiveTags.map((tag) => POSITIVE_LABELS[tag] ?? tag).join(", ")}
                            </p>
                          )}
                          {decision.comment && (
                            <p className="text-[10px] text-muted-foreground whitespace-pre-wrap">
                              {decision.comment}
                            </p>
                          )}
                          {decision.pinX != null && decision.pinY != null && (
                            <p className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
                              <MapPin className="h-2.5 w-2.5" />
                              Pin at {Math.round(decision.pinX * 100)}%, {Math.round(decision.pinY * 100)}%
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {a.adId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => onOpenAd(a.adId!)}
                    aria-label={`Open ${a.displayName} in My Ads`}
                    title="Open in My Ads"
                    data-testid={`button-open-scoreboard-ad-${a.adId}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            );
          })}

          {/* Feedback to incorporate into future work */}
          {reworkAds.length > 0 && (
            <div className="mt-2 rounded-md bg-amber-500/10 p-2 space-y-1">
              <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
                Feedback for future work ({reworkAds.length})
              </p>
              {reworkAds.map((a) => (
                a.adId ? (
                  <button
                    key={a.itemAdId}
                    type="button"
                    className="block w-full text-left text-[11px] text-muted-foreground hover:text-foreground hover:underline truncate"
                    onClick={() => onOpenAd(a.adId!)}
                    data-testid={`button-open-rework-ad-${a.adId}`}
                  >
                    • {a.displayName}
                  </button>
                ) : (
                  <p key={a.itemAdId} className="text-[11px] text-muted-foreground truncate">
                    • {a.displayName} (removed)
                  </p>
                )
              ))}
            </div>
          )}

          {/* Drop queue */}
          {dropAds.length > 0 && (
            <div className="mt-2 rounded-md bg-destructive/10 p-2 space-y-1">
              <p className="text-[11px] font-medium text-destructive">
                Drop queue ({dropAds.length})
              </p>
              {dropAds.map((a) => (
                a.adId ? (
                  <button
                    key={a.itemAdId}
                    type="button"
                    className="block w-full text-left text-[11px] text-muted-foreground hover:text-foreground hover:underline truncate"
                    onClick={() => onOpenAd(a.adId!)}
                    data-testid={`button-open-drop-ad-${a.adId}`}
                  >
                    • {a.displayName}
                  </button>
                ) : (
                  <p key={a.itemAdId} className="text-[11px] text-muted-foreground truncate">
                    • {a.displayName} (removed)
                  </p>
                )
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Data hooks ─────────────────────────────────────────────────────────────

/** All deliveries for a brand (master admin view). */
function useBrandDeliveriesFull(brandId: string, enabled: boolean) {
  const listQuery = useQuery<WeeklyDelivery[]>({
    queryKey: [`/api/brands/${brandId}/deliveries`, "internal-full"],
    enabled: !!brandId && enabled,
    staleTime: 10_000,
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/brands/${brandId}/weekly-deliveries`);
      const raw = await res.json();
      if (!Array.isArray(raw)) return [];
      return raw.map(normalizeDelivery).filter((delivery): delivery is WeeklyDelivery => !!delivery);
    },
  });

  return {
    isLoading: listQuery.isLoading,
    deliveries: listQuery.data ?? [],
  };
}

// ─── Delivery selector item ─────────────────────────────────────────────────

function DeliverySelectorItem({
  delivery,
  isSelected,
  onSelect,
  onCopyReviewLink,
}: {
  delivery: WeeklyDelivery;
  isSelected: boolean;
  onSelect: () => void;
  onCopyReviewLink?: (delivery: WeeklyDelivery) => void;
}) {
  const canShareReviewLink =
    delivery.status === "delivered" || delivery.status === "closed";

  return (
    <div
      className={[
        "flex items-center gap-1 rounded-md text-sm transition-colors",
        isSelected ? "bg-primary/10" : "hover:bg-muted/60",
      ].join(" ")}
      data-testid={`delivery-selector-item-${delivery.id}`}
    >
      <button
        type="button"
        onClick={onSelect}
        className={[
          "min-w-0 flex-1 px-3 py-2 text-left flex items-center justify-between gap-2",
          isSelected ? "text-primary font-medium" : "text-foreground",
        ].join(" ")}
      >
        <span className="min-w-0">
          <span className="block truncate">{delivery.title}</span>
          <span className="block truncate text-[10px] font-normal text-muted-foreground">
            {fmtSelectorDate(delivery)}
          </span>
        </span>
        <Badge
          variant={deliveryStatusVariant(delivery.status)}
          className="text-[10px] shrink-0"
        >
          {deliveryStatusLabel(delivery.status)}
        </Badge>
      </button>
      {canShareReviewLink && onCopyReviewLink && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="mr-1 h-7 w-7 shrink-0"
          onClick={() => onCopyReviewLink(delivery)}
          title={`Copy review link for ${delivery.title}`}
          aria-label={`Copy review link for ${delivery.title}`}
          data-testid={`button-share-delivery-link-${delivery.id}`}
        >
          <Link2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

// ─── Main modal ────────────────────────────────────────────────────────────

export function WeeklyDeliveriesSheet({
  brandId,
  isMaster,
  open,
  onOpenChange,
  projects,
  deepLinkAdId,
  approvalStates,
  initialProjectId = null,
  onInitialProjectHandled,
  deepLinkDeliveryId = null,
  onDeliveryDeepLinkHandled,
  onOpenAd,
}: {
  brandId: string;
  isMaster: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: Project[];
  deepLinkAdId: string | null;
  approvalStates: Map<string, AdApprovalStatus>;
  initialProjectId?: string | null;
  onInitialProjectHandled?: () => void;
  deepLinkDeliveryId?: string | null;
  onDeliveryDeepLinkHandled?: () => void;
  onOpenAd: (adId: string) => void;
}) {
  const { toast } = useToast();
  const [tab, setTab] = useState<"deliveries" | "scoreboard">("deliveries");
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
  const [closingDelivery, setClosingDelivery] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [addingItem, setAddingItem] = useState<string | null>(null);
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [editingDueAt, setEditingDueAt] = useState("");
  const [editingNote, setEditingNote] = useState("");
  const [savingHeader, setSavingHeader] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);

  // Selected delivery ID — null means "use default"
  const [selectedDeliveryId, setSelectedDeliveryId] = useState<string | null>(null);

  // Delete delivery state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletingDelivery, setDeletingDelivery] = useState(false);

  // Revoke delivery state
  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);
  const [revokingDelivery, setRevokingDelivery] = useState(false);

  // Load all boards for the brand (master).
  const { deliveries, isLoading } = useBrandDeliveriesFull(brandId, open && isMaster);

  // Sort deliveries: drafts first (newest), then by status priority and date.
  const sortedDeliveries = useMemo(() => {
    return [...deliveries].sort((a, b) => {
      // Drafts come first
      if (a.status === "draft" && b.status !== "draft") return -1;
      if (b.status === "draft" && a.status !== "draft") return 1;
      // Among same status, newer first
      const aDate = a.createdAt ?? "";
      const bDate = b.createdAt ?? "";
      return bDate.localeCompare(aDate);
    });
  }, [deliveries]);

  // The selected delivery object. Default = newest draft, else newest delivery.
  const delivery = useMemo<WeeklyDelivery | null>(() => {
    if (deliveries.length === 0) return null;
    if (selectedDeliveryId) {
      const found = deliveries.find((d) => d.id === selectedDeliveryId);
      if (found) return found;
    }
    // Default: newest draft first, else most recent by date
    const draft = [...deliveries]
      .filter((d) => d.status === "draft")
      .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""))[0];
    if (draft) return draft;
    return [...deliveries].sort((a, b) =>
      (b.publishedAt ?? b.createdAt ?? "").localeCompare(a.publishedAt ?? a.createdAt ?? ""),
    )[0] ?? null;
  }, [deliveries, selectedDeliveryId]);

  const scoreboardDeliveries = useMemo(
    () =>
      deliveries
        .filter((entry) => entry.status === "delivered" || entry.status === "closed" || entry.status === "revoked")
        .sort((a, b) =>
          (b.publishedAt ?? b.createdAt ?? "").localeCompare(
            a.publishedAt ?? a.createdAt ?? "",
          ),
        ),
    [deliveries],
  );

  const { data: eligible = [], isFetching: eligibleLoading } = useQuery<EligibleProject[]>({
    queryKey: [`/api/brands/${brandId}/weekly-deliveries/eligible-projects`, delivery?.id],
    enabled: !!brandId && open && isMaster && delivery?.status === "draft",
    staleTime: 10_000,
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/brands/${brandId}/weekly-deliveries/eligible-projects`,
      );
      const raw = await res.json();
      if (!Array.isArray(raw)) return [];
      return raw.map((project: RawAny) => ({
        projectId: String(project.projectId),
        projectName: String(project.projectName ?? "Untitled project"),
        adCount: Number(project.adCount ?? 0),
        adIds: Array.isArray(project.thumbnails)
          ? project.thumbnails.map((thumbnail: RawAny) => String(thumbnail.adId))
          : [],
        thumbnails: Array.isArray(project.thumbnails)
          ? project.thumbnails.map((thumbnail: RawAny) => ({
              adId: String(thumbnail.adId),
              imageUrl: thumbnail.imageUrl ?? null,
            }))
          : [],
      }));
    },
  });

  // Initialize header edit fields when delivery loads/changes.
  useEffect(() => {
    if (delivery) {
      setEditingTitle(delivery.title);
      setEditingDueAt(fmtDateInput(delivery.dueAt));
      setEditingNote(delivery.note ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delivery?.id, delivery?.title, delivery?.dueAt]);

  useEffect(() => {
    setSelectedDeliveryId(null);
  }, [brandId]);

  // Deep-link: select the delivery containing the ad, then switch to its
  // scoreboard. This matters now that the same brand may have many boards.
  useEffect(() => {
    if (!deepLinkAdId || !open || isLoading) return;
    const foundDelivery = deliveries.find((entry) => entry.items.some((item) =>
      item.ads.some((a) => a.adId === deepLinkAdId),
    ));
    if (!foundDelivery) return;
    setSelectedDeliveryId(foundDelivery.id);
    setTab("scoreboard");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById("scoreboard-ad-deeplink");
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    });
  }, [deepLinkAdId, open, isLoading, deliveries]);

  // Internal notification deep-link: open the scoreboard and center the
  // referenced delivery.
  useEffect(() => {
    if (!deepLinkDeliveryId || !open || isLoading) return;
    const found = deliveries.find((entry) => entry.id === deepLinkDeliveryId);
    if (found) {
      setSelectedDeliveryId(deepLinkDeliveryId);
      setTab("scoreboard");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document
            .querySelector(
              `[data-testid="scoreboard-delivery-${deepLinkDeliveryId}"]`,
            )
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
          onDeliveryDeepLinkHandled?.();
        });
      });
    } else {
      onDeliveryDeepLinkHandled?.();
    }
  }, [
    deepLinkDeliveryId,
    open,
    isLoading,
    deliveries,
    onDeliveryDeepLinkHandled,
  ]);

  const isDraft = delivery?.status === "draft";
  const isDelivered = delivery?.status === "delivered";
  const isClosed = delivery?.status === "closed";
  const isRevoked = delivery?.status === "revoked";
  // Read-only for closed and revoked
  const isReadOnly = isClosed || isRevoked;

  const copyReviewLink = async (deliveryToShare: WeeklyDelivery) => {
    if (
      deliveryToShare.status !== "delivered" &&
      deliveryToShare.status !== "closed"
    ) {
      return;
    }
    const url = `${window.location.origin}/brands/${encodeURIComponent(brandId)}/deliverables/${encodeURIComponent(deliveryToShare.id)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Review link copied",
        description: "Send it to a client who has access to this brand.",
      });
    } catch {
      toast({
        title: "Couldn't copy review link",
        description: url,
        variant: "destructive",
      });
    }
  };

  // Create a new draft delivery.
  const createDraft = async () => {
    setCreatingDraft(true);
    try {
      const title = defaultDeliveryTitle();
      const res = await apiRequest("POST", `/api/brands/${brandId}/weekly-deliveries`, { title });
      const created = normalizeDelivery(await res.json());
      if (created) {
        setSelectedDeliveryId(created.id);
        setTab("deliveries");
      }
      invalidateDeliveries(brandId);
      toast({ title: "Delivery created", description: "Add projects to get started." });
    } catch (e: unknown) {
      toast({ title: "Couldn't create delivery", description: String(e), variant: "destructive" });
    } finally {
      setCreatingDraft(false);
    }
  };

  // Save header (title + due date).
  const saveHeader = async () => {
    if (!delivery) return;
    const title = editingTitle.trim();
    if (!title) return;
    setSavingHeader(true);
    try {
      await apiRequest("PATCH", `/api/brands/${brandId}/weekly-deliveries/${delivery.id}`, {
        title,
        note: editingNote.trim() || null,
        // A date input has no timezone. Store local noon so the selected
        // calendar day does not shift backward when serialized from US zones.
        dueAt: editingDueAt ? new Date(`${editingDueAt}T12:00:00`).toISOString() : null,
      });
      invalidateDeliveries(brandId);
      toast({ title: "Saved" });
    } catch (e: unknown) {
      toast({ title: "Couldn't save", description: String(e), variant: "destructive" });
    } finally {
      setSavingHeader(false);
    }
  };

  // Add a project (project-level curation: freezes the project's eligible ad
  // set into the delivery). This is the ONLY entry point for adding ads.
  const addProject = async (ep: EligibleProject, note?: string | null) => {
    if (!delivery) return;
    if (ep.adIds.length === 0) {
      toast({
        title: "No eligible ads",
        description: "This project has no saved image ads to deliver.",
        variant: "destructive",
      });
      return;
    }
    setAddingItem(ep.projectId);
    try {
      const addRes = await apiRequest(
        "POST",
        `/api/brands/${brandId}/weekly-deliveries/${delivery.id}/items`,
        { projectId: ep.projectId },
      );
      if (note?.trim()) {
        // The add endpoint returns the refreshed delivery, so resolve the new
        // item from that exact delivery rather than a singleton/current route.
        let addedItemId: string | null = null;
        try {
          const addedRaw = await addRes.json();
          const addedObj = addedRaw?.item ?? addedRaw?.items?.find((item: RawAny) => item.projectId === ep.projectId) ?? null;
          if (addedObj?.id) {
            addedItemId = String(addedObj.id);
          }
        } catch {
          // ignore parse errors
        }
        if (addedItemId) {
          await apiRequest(
            "PATCH",
            `/api/brands/${brandId}/weekly-deliveries/${delivery.id}/items/${addedItemId}`,
            { note: note.trim() },
          );
        }
      }
      invalidateDeliveries(brandId);
      toast({ title: "Project added to delivery" });
    } catch (e: unknown) {
      toast({ title: "Couldn't add project", description: String(e), variant: "destructive" });
    } finally {
      setAddingItem(null);
    }
  };

  const handledInitialProjectRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialProjectId) {
      handledInitialProjectRef.current = null;
      return;
    }
    if (
      !open ||
      isLoading ||
      eligibleLoading ||
      !delivery ||
      handledInitialProjectRef.current === initialProjectId
    ) {
      return;
    }

    setTab("deliveries");
    if (delivery.status !== "draft") {
      handledInitialProjectRef.current = initialProjectId;
      toast({
        title: "Selected delivery is not a draft",
        description: "Switch to a draft delivery or create a new one.",
      });
      onInitialProjectHandled?.();
      return;
    }
    if (delivery.items.some((item) => item.projectId === initialProjectId)) {
      handledInitialProjectRef.current = initialProjectId;
      toast({ title: "Project already added" });
      onInitialProjectHandled?.();
      return;
    }
    const project = eligible.find((entry) => entry.projectId === initialProjectId);
    if (!project) {
      handledInitialProjectRef.current = initialProjectId;
      toast({
        title: "No eligible ads",
        description: "This project has no recently saved, ready image ads.",
        variant: "destructive",
      });
      onInitialProjectHandled?.();
      return;
    }

    handledInitialProjectRef.current = initialProjectId;
    void addProject(project).finally(() => onInitialProjectHandled?.());
    // The callbacks are intentionally handled once for each requested project.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProjectId, open, isLoading, eligibleLoading, delivery?.id, eligible]);

  // Remove a delivery item.
  const removeItem = async (itemId: string) => {
    setRemovingItemId(itemId);
    try {
      await apiRequest(
        "DELETE",
        `/api/brands/${brandId}/weekly-deliveries/${delivery?.id}/items/${itemId}`,
      );
      invalidateDeliveries(brandId);
      toast({ title: "Project removed from delivery" });
    } catch (e: unknown) {
      toast({ title: "Couldn't remove", description: String(e), variant: "destructive" });
    } finally {
      setRemovingItemId(null);
    }
  };

  // Publish (deliver) the draft.
  const publishDraft = async () => {
    if (!delivery) return;
    setPublishing(true);
    setConfirmPublishOpen(false);
    try {
      await apiRequest(
        "POST",
        `/api/brands/${brandId}/weekly-deliveries/${delivery.id}/deliver`,
      );
      invalidateDeliveries(brandId);
      toast({ title: "Delivery published", description: "The client can now review the ads." });
      setTab("scoreboard");
    } catch (e: unknown) {
      toast({ title: "Couldn't publish", description: String(e), variant: "destructive" });
    } finally {
      setPublishing(false);
    }
  };

  // Close delivery (mark complete when all items are done).
  const closeDelivery = async () => {
    if (!delivery) return;
    setClosingDelivery(true);
    try {
      await apiRequest(
        "POST",
        `/api/brands/${brandId}/weekly-deliveries/${delivery.id}/close`,
      );
      invalidateDeliveries(brandId);
      toast({ title: "Delivery closed", description: "The delivery was marked complete." });
    } catch (e: unknown) {
      toast({ title: "Couldn't close delivery", description: String(e), variant: "destructive" });
    } finally {
      setClosingDelivery(false);
    }
  };

  // Delete delivery (all statuses, confirmed).
  const deleteDelivery = async () => {
    if (!delivery) return;
    setDeletingDelivery(true);
    setConfirmDeleteOpen(false);
    try {
      await apiRequest(
        "DELETE",
        `/api/brands/${brandId}/weekly-deliveries/${delivery.id}`,
      );
      // Select another delivery or clear selection
      const remaining = deliveries.filter((d) => d.id !== delivery.id);
      if (remaining.length > 0) {
        const nextDraft = remaining.find((d) => d.status === "draft");
        setSelectedDeliveryId(nextDraft?.id ?? remaining[0].id);
      } else {
        setSelectedDeliveryId(null);
      }
      invalidateDeliveries(brandId);
      toast({ title: "Delivery deleted" });
    } catch (e: unknown) {
      toast({ title: "Couldn't delete delivery", description: String(e), variant: "destructive" });
    } finally {
      setDeletingDelivery(false);
    }
  };

  // Revoke delivery (delivered status only, confirmed).
  const revokeDelivery = async () => {
    if (!delivery) return;
    setRevokingDelivery(true);
    setConfirmRevokeOpen(false);
    try {
      await apiRequest(
        "POST",
        `/api/brands/${brandId}/weekly-deliveries/${delivery.id}/revoke`,
      );
      invalidateDeliveries(brandId);
      toast({ title: "Delivery revoked", description: "Client access has been removed." });
    } catch (e: unknown) {
      toast({ title: "Couldn't revoke delivery", description: String(e), variant: "destructive" });
    } finally {
      setRevokingDelivery(false);
    }
  };

  // Whether all items in a delivered board are fully reviewed (no unanswered).
  const allItemsReviewed = useMemo(() => {
    if (!delivery || !isDelivered) return false;
    return delivery.items.length > 0 && delivery.items.every(
      (item) => !!item.submittedAt || !!item.projectRejectedAt,
    );
  }, [delivery, isDelivered]);

  // Publish summary for the confirm dialog.
  const publishSummary = useMemo(() => {
    if (!delivery) return null;
    const totalAds = delivery.items.reduce((n, i) => n + i.ads.length, 0);
    return {
      projectCount: delivery.items.length,
      adCount: totalAds,
      dueAt: delivery.dueAt,
    };
  }, [delivery]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="!flex h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] min-w-0 !max-w-5xl flex-col !gap-0 overflow-hidden !p-0 sm:h-[min(52rem,calc(100dvh-2rem))]"
          data-testid="dialog-weekly-deliveries"
        >
          <DialogHeader className="min-w-0 shrink-0 border-b pb-3 pl-4 pr-12 pt-6 sm:pl-6">
            <DialogTitle className="flex min-w-0 flex-wrap items-center gap-2">
              <PackageOpen className="h-5 w-5" />
              Deliverables
              {delivery && (
                <Badge
                  variant={deliveryStatusVariant(delivery.status)}
                  className="text-[11px] ml-1"
                  data-testid="badge-delivery-status"
                >
                  {deliveryStatusLabel(delivery.status)}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Assemble project ads into a delivery and review client feedback.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-1 min-h-0 flex-col overflow-hidden sm:flex-row">
            {/* ── Delivery selector sidebar ─────────────────────────────── */}
            {isMaster && (
              <div
                className="max-h-40 w-full shrink-0 border-b bg-muted/20 flex flex-col overflow-y-auto overscroll-contain sm:max-h-none sm:w-52 sm:border-b-0 sm:border-r"
                data-testid="delivery-selector-panel"
              >
                <div className="px-3 pt-3 pb-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-7 text-xs"
                    onClick={createDraft}
                    disabled={creatingDraft}
                    data-testid="button-new-delivery"
                  >
                    {creatingDraft ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <Plus className="h-3.5 w-3.5 mr-1" />
                    )}
                    New delivery
                  </Button>
                </div>
                <div className="px-2 pb-3 flex-1">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  ) : sortedDeliveries.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground text-center py-4 px-2">
                      No deliveries yet
                    </p>
                  ) : (
                    <div className="space-y-0.5">
                      {sortedDeliveries.map((d) => (
                        <DeliverySelectorItem
                          key={d.id}
                          delivery={d}
                          isSelected={delivery?.id === d.id}
                          onSelect={() => setSelectedDeliveryId(d.id)}
                          onCopyReviewLink={copyReviewLink}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Main panel ─────────────────────────────────────────────── */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
              {/* Tab switcher */}
              {delivery && (
                <div className="flex shrink-0 flex-wrap gap-1 px-4 pb-2 pt-3 sm:px-6 border-b items-center justify-between">
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant={tab === "deliveries" ? "secondary" : "ghost"}
                      className="h-7 text-xs"
                      onClick={() => setTab("deliveries")}
                      data-testid="tab-deliveries"
                    >
                      <PackageOpen className="h-3.5 w-3.5 mr-1" />
                      Deliverables
                    </Button>
                    <Button
                      size="sm"
                      variant={tab === "scoreboard" ? "secondary" : "ghost"}
                      className="h-7 text-xs"
                      onClick={() => setTab("scoreboard")}
                      data-testid="tab-scoreboard"
                    >
                      <BarChart3 className="h-3.5 w-3.5 mr-1" />
                      Client feedback
                    </Button>
                  </div>
                  {/* Danger actions in header row */}
                  {isMaster && delivery && (
                    <div className="flex items-center gap-1">
                      {(isDelivered || isClosed) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => copyReviewLink(delivery)}
                          title="Copy client review link"
                          data-testid="button-share-delivery-link"
                        >
                          <Link2 className="h-3.5 w-3.5 mr-1" />
                          Share link
                        </Button>
                      )}
                      {isDelivered && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          onClick={() => setConfirmRevokeOpen(true)}
                          disabled={revokingDelivery}
                          data-testid="button-revoke-delivery"
                        >
                          <Ban className="h-3.5 w-3.5 mr-1" />
                          Revoke
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setConfirmDeleteOpen(true)}
                        disabled={deletingDelivery}
                        data-testid="button-delete-delivery"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="min-w-0 flex-1 space-y-4 overflow-x-hidden overflow-y-auto overscroll-contain px-4 pb-6 pt-2 sm:px-6">
                {isLoading ? (
                  <div className="flex items-center justify-center py-16 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Loading…
                  </div>
                ) : !delivery ? (
                  // No delivery yet — create one.
                  <div className="text-center py-12 space-y-3" data-testid="empty-delivery">
                    <PackageOpen className="h-8 w-8 mx-auto text-muted-foreground" />
                    <p className="text-sm font-medium">No deliveries yet</p>
                    <p className="text-xs text-muted-foreground">
                      Create a new delivery to start assembling ads for review.
                    </p>
                    {isMaster && (
                      <Button
                        size="sm"
                        onClick={createDraft}
                        disabled={creatingDraft}
                        data-testid="button-create-draft-delivery"
                      >
                        {creatingDraft ? (
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        ) : (
                          <Plus className="h-3.5 w-3.5 mr-1" />
                        )}
                        New delivery
                      </Button>
                    )}
                  </div>
                ) : tab === "deliveries" ? (
                  // ── Deliveries tab ──────────────────────────────────────────────
                  <div className="space-y-4">
                    {/* Revoked notice */}
                    {isRevoked && (
                      <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 flex items-center gap-2">
                        <Ban className="h-4 w-4 text-destructive shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-destructive">Revoked</p>
                          <p className="text-xs text-muted-foreground">
                            Client access has been removed. Internal history is preserved read-only.
                          </p>
                        </div>
                        <Badge variant="destructive" className="ml-auto shrink-0">Revoked</Badge>
                      </div>
                    )}

                    {/* Header edit (master only, draft only) */}
                    {isMaster && isDraft && (
                      <Card className="p-3 space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">Delivery settings</p>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          <div className="space-y-1">
                            <label className="text-[11px] text-muted-foreground">Title</label>
                            <Input
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              className="h-8 text-sm"
                              placeholder="e.g. Delivery — Jul 28"
                              data-testid="input-delivery-title"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[11px] text-muted-foreground">Due date</label>
                            <Input
                              type="date"
                              value={editingDueAt}
                              onChange={(e) => setEditingDueAt(e.target.value)}
                              className="h-8 text-sm"
                              data-testid="input-delivery-due"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-[11px] text-muted-foreground">Team note</label>
                          <Textarea
                            value={editingNote}
                            onChange={(e) => setEditingNote(e.target.value)}
                            rows={3}
                            maxLength={4000}
                            placeholder="Add context for the client reviewers…"
                            data-testid="input-delivery-note"
                          />
                        </div>
                        <div className="flex justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={saveHeader}
                            disabled={savingHeader || !editingTitle.trim()}
                            className="h-7 text-xs"
                            data-testid="button-save-delivery-header"
                          >
                            {savingHeader ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
                          </Button>
                        </div>
                      </Card>
                    )}

                    {/* Delivery header (read mode) */}
                    {(!isMaster || !isDraft) && !isRevoked && (
                      <div className="space-y-0.5">
                        <p className="text-base font-semibold">{delivery.title}</p>
                        {delivery.dueAt && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Due {fmtDate(delivery.dueAt)}
                          </p>
                        )}
                        {delivery.publishedAt && (
                          <p className="text-xs text-muted-foreground">
                            Published {fmtDate(delivery.publishedAt)}
                          </p>
                        )}
                        {delivery.note && (
                          <p className="text-sm text-muted-foreground pt-2 whitespace-pre-wrap">
                            {delivery.note}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Items in delivery */}
                    {delivery.items.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Projects in this delivery
                        </p>
                        {delivery.items.map((item) => (
                          <Card
                            key={item.id}
                            className="p-3 space-y-2"
                            data-testid={`delivery-item-${item.id}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{item.projectName}</p>
                                <p className="text-[11px] text-muted-foreground">
                                  {item.ads.length} ad{item.ads.length === 1 ? "" : "s"}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {/* Thumbnails from frozen snapshots */}
                                <div className="flex gap-0.5">
                                  {item.ads.slice(0, 3).map((a) => (
                                    <AdThumbnail
                                      key={a.itemAdId}
                                      src={a.imageUrl}
                                      alt={a.displayName}
                                      size={32}
                                    />
                                  ))}
                                  {item.ads.length > 3 && (
                                    <div className="w-8 h-8 rounded bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground shrink-0">
                                      +{item.ads.length - 3}
                                    </div>
                                  )}
                                </div>
                                {isMaster && isDraft && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
                                    onClick={() => removeItem(item.id)}
                                    disabled={removingItemId === item.id}
                                    data-testid={`button-remove-item-${item.id}`}
                                  >
                                    {removingItemId === item.id ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <Minus className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                            {/* Team note (read-only display — set at add time) */}
                            {item.note && (
                              <p className="text-xs text-muted-foreground italic">{item.note}</p>
                            )}
                          </Card>
                        ))}
                      </div>
                    )}

                    {/* Suggest eligible projects (draft mode, master only) */}
                    {isMaster && isDraft && eligible.length > 0 && (
                      <div className="space-y-2">
                        <Separator />
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Suggested projects to add
                        </p>
                        {eligible.map((ep) => {
                          const alreadyIn = delivery.items.some(
                            (i) => i.projectId === ep.projectId,
                          );
                          if (alreadyIn) return null;
                          return (
                            <Card
                              key={ep.projectId}
                              className="p-3 flex items-center justify-between gap-2"
                              data-testid={`eligible-project-${ep.projectId}`}
                            >
                              <div className="min-w-0 flex items-center gap-2">
                                <div className="flex gap-0.5">
                                  {ep.thumbnails.slice(0, 3).map((t) => (
                                    <AdThumbnail
                                      key={t.adId}
                                      src={t.imageUrl}
                                      alt=""
                                      size={32}
                                    />
                                  ))}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate">{ep.projectName}</p>
                                  <p className="text-[11px] text-muted-foreground">
                                    {ep.adCount} eligible ad{ep.adCount === 1 ? "" : "s"}
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs shrink-0"
                                disabled={addingItem === ep.projectId}
                                onClick={() => addProject(ep)}
                                data-testid={`button-add-project-${ep.projectId}`}
                              >
                                {addingItem === ep.projectId ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Plus className="h-3.5 w-3.5 mr-1" />
                                )}
                                Add
                              </Button>
                            </Card>
                          );
                        })}
                      </div>
                    )}

                    {/* Empty state (draft with no items) */}
                    {isDraft && delivery.items.length === 0 && eligible.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No projects with saved ads found. Save some ads to projects first.
                      </p>
                    )}

                    {/* Publish action (master, draft, has items) */}
                    {isMaster && isDraft && delivery.items.length > 0 && (
                      <div className="pt-2">
                        <Separator className="mb-4" />
                        <div className="rounded-md bg-muted/60 p-3 space-y-2">
                          <p className="text-xs font-medium">Publish summary</p>
                          <p className="text-sm">
                            {delivery.items.length} project{delivery.items.length === 1 ? "" : "s"} ·{" "}
                            {delivery.items.reduce((n, i) => n + i.ads.length, 0)} ads
                            {delivery.dueAt && <> · Due {fmtDate(delivery.dueAt)}</>}
                          </p>
                          <Button
                            size="sm"
                            onClick={() => setConfirmPublishOpen(true)}
                            disabled={publishing}
                            className="h-8"
                            data-testid="button-publish-delivery"
                          >
                            {publishing ? (
                              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            ) : (
                              <Send className="h-3.5 w-3.5 mr-1" />
                            )}
                            Publish for review
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Delivered / closed / revoked info */}
                    {(isDelivered || isClosed || isRevoked) && (
                      <div className="rounded-md border p-3 space-y-1">
                        <p className="text-sm font-medium flex items-center gap-1.5">
                          {isClosed ? (
                            <Lock className="h-4 w-4 text-muted-foreground" />
                          ) : isRevoked ? (
                            <Ban className="h-4 w-4 text-destructive" />
                          ) : (
                            <Send className="h-4 w-4 text-primary" />
                          )}
                          {isClosed
                            ? "Delivery closed"
                            : isRevoked
                            ? "Delivery revoked"
                            : "Delivery in review"}
                        </p>
                        {delivery.publishedAt && (
                          <p className="text-xs text-muted-foreground">
                            Published {fmtDate(delivery.publishedAt)}
                          </p>
                        )}
                        {delivery.closedAt && (
                          <p className="text-xs text-muted-foreground">
                            Closed {fmtDate(delivery.closedAt)}
                          </p>
                        )}
                        {isMaster && isDelivered && allItemsReviewed && (
                          <div className="pt-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={closeDelivery}
                              disabled={closingDelivery}
                              data-testid="button-close-delivery"
                            >
                              {closingDelivery ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                              ) : (
                                <Lock className="h-3.5 w-3.5 mr-1" />
                              )}
                              Close delivery
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  // ── Scoreboard tab ───────────────────────────────────────────────
                  <div className="space-y-4" data-testid="scoreboard-tab">
                    {/* Show the selected delivery's scoreboard, or all published for context */}
                    {delivery && (delivery.status === "delivered" || delivery.status === "closed" || delivery.status === "revoked") ? (
                      <section
                        className="space-y-2"
                        data-testid={`scoreboard-delivery-${delivery.id}`}
                        id={
                          deepLinkDeliveryId === delivery.id
                            ? "scoreboard-delivery-deeplink"
                            : undefined
                        }
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold">{delivery.title}</p>
                            <p className="text-[11px] text-muted-foreground">
                              {delivery.publishedAt
                                ? `Published ${fmtDate(delivery.publishedAt)}`
                                : "Published delivery"}
                            </p>
                          </div>
                          <Badge variant={deliveryStatusVariant(delivery.status)}>
                            {deliveryStatusLabel(delivery.status)}
                          </Badge>
                        </div>
                        {isRevoked && (
                          <div className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 flex items-center gap-2 text-xs text-destructive">
                            <Ban className="h-3.5 w-3.5 shrink-0" />
                            This delivery is revoked. Client access removed. Internal history preserved read-only.
                          </div>
                        )}
                        {delivery.items.map((item) => (
                          <ProjectScoreCard
                            key={item.id}
                            item={item}
                            approvalStates={approvalStates}
                            deepLinkAdId={deepLinkAdId}
                            onOpenAd={onOpenAd}
                          />
                        ))}
                        {delivery.items.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No projects in this delivery.
                          </p>
                        )}
                      </section>
                    ) : delivery && delivery.status === "draft" ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        This delivery is still a draft. Publish it to see client feedback here.
                      </p>
                    ) : scoreboardDeliveries.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No published deliveries yet.
                      </p>
                    ) : null}

                    {/* Global close action when all items fully reviewed */}
                    {isMaster && isDelivered && allItemsReviewed && (
                      <div className="pt-2 border-t">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={closeDelivery}
                          disabled={closingDelivery}
                          data-testid="button-close-delivery-scoreboard"
                        >
                          {closingDelivery ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                          ) : (
                            <Lock className="h-3.5 w-3.5 mr-1" />
                          )}
                          Close delivery (all reviewed)
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Publish confirm dialog */}
      <Dialog open={confirmPublishOpen} onOpenChange={setConfirmPublishOpen}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-confirm-publish">
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            Publish delivery?
          </DialogTitle>
          <DialogDescription>
            Review the frozen project and ad counts before sharing this delivery with brand reviewers.
          </DialogDescription>
          {publishSummary && (
            <div className="space-y-3 pt-1">
              <p className="text-sm text-muted-foreground">
                This will publish <strong>{publishSummary.projectCount} project{publishSummary.projectCount === 1 ? "" : "s"}</strong> and{" "}
                <strong>{publishSummary.adCount} ad{publishSummary.adCount === 1 ? "" : "s"}</strong> to the client review page.
                {publishSummary.dueAt && (
                  <> The review is due by <strong>{fmtDate(publishSummary.dueAt)}</strong>.</>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                Once published, the delivery is locked for editing. Client feedback will appear here
                as reviewers submit their decisions.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirmPublishOpen(false)}
                  data-testid="button-cancel-publish"
                >
                  Cancel
                </Button>
                <Button
                  onClick={publishDraft}
                  disabled={publishing}
                  data-testid="button-confirm-publish"
                >
                  {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                  Publish
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete delivery confirm dialog */}
      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-confirm-delete">
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-4 w-4" />
            Delete delivery?
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone.
          </DialogDescription>
          <div className="space-y-3 pt-1">
            <p className="text-sm text-muted-foreground">
              <strong>Frozen review decisions will be permanently removed.</strong>{" "}
              Source ads and projects in your library are not affected.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmDeleteOpen(false)}
                data-testid="button-cancel-delete"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={deleteDelivery}
                disabled={deletingDelivery}
                data-testid="button-confirm-delete"
              >
                {deletingDelivery ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
                Delete delivery
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revoke delivery confirm dialog */}
      <Dialog open={confirmRevokeOpen} onOpenChange={setConfirmRevokeOpen}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-confirm-revoke">
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <Ban className="h-4 w-4" />
            Revoke delivery?
          </DialogTitle>
          <DialogDescription>
            Remove client access to this delivery.
          </DialogDescription>
          <div className="space-y-3 pt-1">
            <p className="text-sm text-muted-foreground">
              Revoking removes client access immediately. Internal read-only history is preserved —
              review decisions, project feedback, and the delivery timeline remain visible to your team.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmRevokeOpen(false)}
                data-testid="button-cancel-revoke"
              >
                Cancel
              </Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={revokeDelivery}
                disabled={revokingDelivery}
                data-testid="button-confirm-revoke"
              >
                {revokingDelivery ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Ban className="h-4 w-4 mr-1" />}
                Revoke delivery
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
